







































































































import { Component, Mixins, Watch } from 'vue-property-decorator'
import { IMPORT_TYPES } from '@/classes/library'
import { ensureArray } from '@/helpers/generic'
import { parseAndImportLibraryStreaming, ImportError, parsePlaylistsXml } from '@/helpers/import/uploader'
import { LIBRARY_IMPORT_STATE } from '@/store/modules/library'
import { sentryMessage } from '../../../system/sentry'
import JSZip from 'jszip'
import { IVirtualDjXmlPlaylist } from '../../../interfaces/virtualdjxml'
import { PLAYLIST_TYPES } from '../../../classes/playlist'
import { IRekordboxFolderNode, IRekordboxPlaylistNode } from '../../../interfaces/rekordboxxml'
import { ITraktorNode } from '../../../interfaces/traktornml'
import Axios from 'axios'
import { BATCH_SIZE } from '../../../constants'
import { LibraryMixin } from '@/mixins'

enum STATES {
  Init = 'init',
  LoadingPlaylists = 'loading_playlists',
  Playlists = 'playlists'
}

interface INodePlaylist {
  _id: string
  name: string
  count: number
  path: string[]
  type: PLAYLIST_TYPES
  playlists: INodePlaylist[]
}

function getTotalTrackCountRecursive(playlists: INodePlaylist[]): number {
  let count: number = 0

  for (const playlist of playlists) {
    count += playlist.count

    if (playlist.playlists) {
      count += getTotalTrackCountRecursive(playlist.playlists)
    }
  }

  return count
}

@Component({
  components: {
    PlaylistSelector: require('@/components/PlaylistSelector').default,
    HelpCircle: require('@/components/HelpCircle').default,
    MiniLoader: require('@/components/MiniLoader').default
  }
})
export default class ImportLibrary extends Mixins(LibraryMixin) {
  state: STATES = STATES.Init
  files: File[] = []
  isPartialUpload: boolean = false
  file: File | null = null
  selectedPlaylists: INodePlaylist[] = []
  playlists: INodePlaylist[] = []
  playlistNodesOriginal: (IRekordboxFolderNode | ITraktorNode)[]

  get importState() {
    return this.$store.state.library.importState
  }

  get importPercentage(): number {
    return Math.round(this.$store.state.library.importProgressPercentage)
  }

  get totalTrackCount() {
    return getTotalTrackCountRecursive(this.selectedPlaylists)
  }

  get partialValid(): boolean {
    return this.selectedPlaylists.length > 0 && this.totalTrackCount > 0
  }

  get isBrowserEdge(): boolean {
    return navigator.userAgent.toLowerCase().includes('edge')
  }

  get uploadAccept(): string {
    return '.xml, .nml, .zip, .json'
  }

  removeUnselectedNodesAndFillMapRecursive(nodes: any[], parentPath: string[], importType: IMPORT_TYPES, playlistEntrySet: Set<string>): any[] {
    nodes = ensureArray(nodes)

    for (let i = 0; i < nodes.length;) {
      const path: string[] = Object.assign([], parentPath)

      if (importType === IMPORT_TYPES.Rekordbox) path.push(nodes[i].$.Name)
      else if (importType === IMPORT_TYPES.Traktor) path.push(nodes[i].$.NAME)

      if (this.selectedPlaylists.some(x => x.path.slice(0, path.length).every((p, pindex) => p === path[pindex]))) {
        if (importType === IMPORT_TYPES.Rekordbox) {
          if (nodes[i].NODE) {
            nodes[i].NODE = this.removeUnselectedNodesAndFillMapRecursive(nodes[i].NODE, path, importType, playlistEntrySet)
          }

          if (nodes[i].TRACK) {
            for (const track of ensureArray(nodes[i].TRACK)) {
              playlistEntrySet.add(track.$.Key)
            }
          }
        } else if (importType === IMPORT_TYPES.Traktor) {
          if (nodes[i].$.TYPE === 'FOLDER') {
            nodes[i].SUBNODES[0].NODE = this.removeUnselectedNodesAndFillMapRecursive(nodes[i].SUBNODES[0].NODE, path, importType, playlistEntrySet)
          }

          if (nodes[i].$.TYPE === 'PLAYLIST') {
            for (const entry of ensureArray(nodes[i].PLAYLIST[0].ENTRY)) {
              playlistEntrySet.add(entry.PRIMARYKEY[0].$.KEY)
            }
          }
        }

        i++
      } else {
        nodes.splice(i, 1)
      }
    }

    return nodes
  }

  setState(s: STATES) {
    this.state = s
  }

  async loadFile(files: File[] | null) {
    if (!files || files.length === 0) return // If something that isnt a file gets dragged over it, null comes back

    this.file = files[0]

    if (!this.isPartialUpload) {
      await this.upload()
    } else {
      this.setState(STATES.LoadingPlaylists)

      try {
        await this.loadPlaylists()

        this.setState(STATES.Playlists)
      } catch (error) {
        this.setState(STATES.Init)

        console.error(error)
      }
    }
  }

  async loadPlaylists() {
    if (!this.file) return

    let importType: IMPORT_TYPES

    if (this.file.name.toLowerCase().endsWith('.xml')) importType = IMPORT_TYPES.Rekordbox
    else if (this.file.name.toLowerCase().endsWith('.nml')) importType = IMPORT_TYPES.Traktor
    else if (this.file.name.toLowerCase().endsWith('.zip')) {
      window.alert('Partial upload is not supported for VirtualDJ because VirtualDJ cannot import partial libraries. Please use a full upload for VirtualDJ libraries.')

      throw new Error('virtualdj zip not supported for partial upload')
    } else {
      window.alert('This file type is not supported. Please upload an XML, NML or ZIP file. Check the wiki page for more instructions.')

      throw new Error('unexpected filetype')
    }

    let playlistNodes: (IRekordboxFolderNode | ITraktorNode)[] = await parsePlaylistsXml(this.file.name, this.file, importType)

    // @ts-ignore
    this.playlistNodesOriginal = Object.freeze(playlistNodes)

    const isTraktor = this.file.name.toLowerCase().endsWith('.nml')

    let playlistId: number = 0
    function getPlaylistsRecurive(nodes: (IRekordboxFolderNode | IRekordboxPlaylistNode | ITraktorNode)[], path: string[]): INodePlaylist[] {
      nodes = ensureArray(nodes)

      const playlists: INodePlaylist[] = []

      for (let node of nodes) {
        if (node) {
          let playlist: INodePlaylist
          const _id: string = (++playlistId).toString()

          if (isTraktor) {
            node = node as ITraktorNode

            const newPath: string[] = Object.assign([], path)
            newPath.push(node.$.NAME)

            let count: number = 0

            if (node.PLAYLIST) count = parseInt(node.PLAYLIST[0].$.ENTRIES, 10)

            playlist = {
              _id,
              name: node.$.NAME + (node.$.TYPE === 'PLAYLIST' ? ` (${count})` : ''),
              type: node.$.TYPE === 'PLAYLIST' ? PLAYLIST_TYPES.Playlist : PLAYLIST_TYPES.Folder,
              count,
              playlists: [],
              path: newPath
            }

            if (playlist.type === PLAYLIST_TYPES.Folder && node.SUBNODES) {
              playlist.playlists = getPlaylistsRecurive(node.SUBNODES[0].NODE, newPath)
            }
          } else {
            node = node as IRekordboxFolderNode | IRekordboxPlaylistNode

            const newPath: string[] = Object.assign([], path)
            newPath.push(node.$.Name)

            let count: number = 0

            if ((node as IRekordboxPlaylistNode).TRACK) count = parseInt((node as IRekordboxPlaylistNode).$.Entries, 10)

            playlist = {
              _id,
              name: node.$.Name + (node.$.Type === '1' ? ` (${count})` : ''),
              type: node.$.Type === '1' ? PLAYLIST_TYPES.Playlist : PLAYLIST_TYPES.Folder,
              count,
              playlists: [],
              path: newPath
            }

            if (playlist.type === PLAYLIST_TYPES.Folder) {
              playlist.playlists = getPlaylistsRecurive((node as IRekordboxFolderNode).NODE, newPath)
            }
          }

          playlists.push(playlist)
        }
      }

      return playlists
    }

    let rootName: string
    if (isTraktor) {
      rootName = (playlistNodes as ITraktorNode[])[0].$.NAME

      if (rootName === '$ROOT') {
        playlistNodes = (playlistNodes as any[])[0].SUBNODES[0].NODE
      }
    } else {
      rootName = (playlistNodes as IRekordboxFolderNode[])[0].$.Name

      if (rootName === 'ROOT') {
        playlistNodes = (playlistNodes as IRekordboxFolderNode[])[0].NODE as IRekordboxFolderNode[]
      }
    }

    // @ts-ignore
    this.playlists = Object.freeze(getPlaylistsRecurive(playlistNodes, [rootName]))
  }

  async upload() {
    if (!this.file) return
    if (this.$store.state.auth.isDemoUser) return

    const file = this.file

    const filename: string = file.name

    this.setState(STATES.Init)

    this.setNavigationWarning(true)

    this.$store.commit('library/setImportProgressPercentage', 0)
    this.$store.commit('library/setLibraryImportState', LIBRARY_IMPORT_STATE.Uploading)

    this.$store.commit('library/setLibrary', { data: null })
    this.$store.commit('library/clearCache')

    try {
      if (file.name.toLowerCase().endsWith('.zip')) { // VirtualDJ
        const zip = await JSZip.loadAsync(file)

        const databaseXmlZipObject = zip.file('database.xml')

        if (!databaseXmlZipObject) {
          window.alert('This does not appear to be a valid VirtualDJ backup zip file. Please check the wiki page for instructions.')

          throw new ImportError('zip content does not appear to be valid a virtualdj file')
        }

        const databaseXmlContents: string = await databaseXmlZipObject.async('text')

        if (!databaseXmlContents.includes('VirtualDJ_Database')) {
          window.alert('This does not appear to be a valid VirtualDJ backup zip file. Please check the wiki page for instructions.')

          throw new ImportError('xml contents does not appear to be valid virtualdj xml')
        }

        const totalSongCount: number = ((databaseXmlContents || '').match(/<Song /g) || []).length

        const databaseXmlFile = new File([databaseXmlContents], 'database.xml', {
          type: 'text/plain'
        })

        const playlists: IVirtualDjXmlPlaylist[] = []

        const playlistsRaw = zip.filter(function (relativePath) {
          return relativePath.indexOf('Playlists/') === 0
        })

        for (const pl of playlistsRaw) {
          playlists.push({
            Name: pl.name,
            Contents: await pl.async('text')
          })
        }

        if (playlists.length === 0) { // If there is no Playlists folder, add the root playlist folder manually
          playlists.push({
            Name: 'Playlists/',
            Contents: ''
          })
        }

        const historyRaw = zip.filter(function (relativePath) {
          return relativePath.indexOf('History/') === 0
        })

        for (const pl of historyRaw) {
          playlists.push({
            Name: `Playlists/VirtualDJ ${pl.name}`, // Note: downloader matches this string
            Contents: await pl.async('text')
          })
        }

        await parseAndImportLibraryStreaming(filename, databaseXmlFile, IMPORT_TYPES.VirtualDJ, {
          vdjPlaylists: playlists,
          vdjTotalNodes: totalSongCount,
          isPartialImport: false // VDJ does not support this (well it does but it doesn't support partial loading into VDJ, which makes partial upload to rekordcloud useless for VDJ users)
        })

        this.$store.commit('library/setLibraryImportState', LIBRARY_IMPORT_STATE.Done)
      } else if (file.name.toLowerCase().endsWith('.xml') || file.name.toLowerCase().endsWith('.nml')) {
        // Traktor & Rekordbox (also VDJ database.xml but user should not upload that, checked below)

        let importType: IMPORT_TYPES

        if (file.name.toLowerCase().endsWith('.xml')) importType = IMPORT_TYPES.Rekordbox
        else if (file.name.toLowerCase().endsWith('.nml')) importType = IMPORT_TYPES.Traktor
        else throw new Error('unexpected filetype')

        let playlistNodesForPartial: (IRekordboxFolderNode | ITraktorNode)[] = []

        const playlistEntrySet: Set<string> = new Set<string>()
        if (this.isPartialUpload) {
          playlistNodesForPartial = this.removeUnselectedNodesAndFillMapRecursive(
            JSON.parse(JSON.stringify(this.playlistNodesOriginal)),
            [],
            importType,
            playlistEntrySet
          )
        }

        await parseAndImportLibraryStreaming(filename, file, importType, {
          isPartialImport: this.isPartialUpload,
          playlistEntrySet,
          playlistNodesForPartial
        })

        this.$store.commit('library/setLibraryImportState', LIBRARY_IMPORT_STATE.Done)
      } else if (filename.endsWith('.json')) { // Serato upload for JSON libraries. Users don't normally do this.
        const reader = new FileReader()

        const library: any = await new Promise((resolve) => {
          reader.onload = function (event) {
            const jsonString: string = (event.target as any).result

            const json = JSON.parse(jsonString)

            resolve(json)
          }

          reader.readAsText(file)
        })

        if (!library.hasOwnProperty('playlists') || !library.hasOwnProperty('tracks')) {
          window.alert('This is not a valid library file')

          throw new ImportError('invalid json')
        }

        await Axios.post('/library/start-library-import', {
          filename,
          importType: IMPORT_TYPES.Serato,
          isPartialImport: false
        })

        let batchNumber: number = 0

        while (batchNumber * BATCH_SIZE < library.tracks.length) {
          await Axios.post('/library/import-track-batch', {
            tracks: library.tracks.slice(batchNumber * BATCH_SIZE, batchNumber * BATCH_SIZE + BATCH_SIZE),
            importType: IMPORT_TYPES.Serato,
            batchNumber
          })

          batchNumber++

          this.$store.commit('library/setImportProgressPercentage', 100 / (library.tracks.length / (batchNumber * BATCH_SIZE)))
        }

        library.tracks = []

        await Axios.post('/library/finish-library-import', {
          library,
          importType: IMPORT_TYPES.Serato
        })

        await this.$store.dispatch('library/fetchMetadata', { force: true }) // Must force new metadata or it has the timestamp of the previous library

        await this.$store.dispatch('library/fetchLibrary')

        this.$store.commit('library/setLibraryImportState', LIBRARY_IMPORT_STATE.Done)
      } else {
        window.alert('This file could not be recognized as Traktor, Rekordbox or VirtualDJ database. Please check the wiki page for instructions.')
      }
    } catch (error) {
      this.$store.commit('library/setLibraryImportState', LIBRARY_IMPORT_STATE.Init)

      console.error(error)

      if (error instanceof ImportError) {
        // Expected error, don't report
      } else {
        sentryMessage(error, {
          filename: file.name
        })

        if (error.message === 'Network Error') {
          window.alert('A network error occurred. Maybe your WiFi or internet got disconnected? Please reload the page and try again.')
        } else {
          window.alert(`Something went wrong during the upload process. Please try again. If this persists, don't hesitate to contact me.`)
        }
      }
    } finally {
      this.setNavigationWarning(false)
    }
  }

  setNavigationWarning(v: boolean) {
    if (v) {
      window.onbeforeunload = function () {
        return true
      }
    } else {
      window.onbeforeunload = null
    }
  }

  created() {
    if (this.importState === LIBRARY_IMPORT_STATE.Done) {
      this.$store.commit('library/setLibraryImportState', LIBRARY_IMPORT_STATE.Init)
    }
  }

  @Watch('state')
  watchState(s: STATES) {
    if (s === STATES.Init) {
      this.playlists = []
    }
  }
}
